// Helpers puros das telas de Orçamento e Relatórios (montagem de árvore e das séries dos gráficos).
// Sem React — testados em reportHelpers.test.ts.
import type { AnnualCategory, BudgetItem, CashflowMonth, ExpenseCategory } from '@/types/finance';
import { monthKeyLabel } from '@/utils/competence';
import { OTHERS_LABEL, topNWithOthers } from '@/utils/reportUtils';

// ---------------------------------------------------------------- orçamento

export interface BudgetNode {
  item: BudgetItem;
  children: BudgetItem[];
}

/** Lista plana do relatório → raízes (ordem da API) com filhas (ordem da API). Filha sem pai na lista vira raiz. */
export const buildBudgetTree = (items: BudgetItem[]): BudgetNode[] => {
  const ids = new Set(items.map((i) => i.category_id));
  const roots = items.filter((i) => !i.parent_category_id || !ids.has(i.parent_category_id));
  return roots.map((root) => ({ item: root, children: items.filter((c) => c.parent_category_id === root.category_id) }));
};

// ---------------------------------------------------------------- despesas do mês

export interface DonutSliceData {
  /** id da categoria (0 = "Sem categoria") ou "others". */
  key: number | string;
  name: string;
  color?: string | null;
  value: number;
}

/** Categorias raiz → fatias da rosca: as `n` maiores + "Outras". */
export const expenseSlices = (categories: ExpenseCategory[], n: number): DonutSliceData[] =>
  topNWithOthers(
    categories.map((c) => ({ name: c.name, color: c.color, amount_cents: c.amount_cents, id: c.category_id ?? 0 })),
    n,
  ).map((s) => ({ key: 'id' in s ? (s as { id: number }).id : 'others', name: s.name, color: s.color, value: s.amount_cents }));

// ---------------------------------------------------------------- anual

export interface ChartSeriesDef {
  key: string;
  name: string;
  color?: string | null;
}

/**
 * Séries do gráfico anual: as `n` maiores categorias (por total) + "Outras" (soma das demais, mês
 * a mês). `rows` tem uma linha por mês com `label` ("Jan") e uma coluna por série (`c<id>` / `others`).
 */
export const annualChartSeries = (categories: AnnualCategory[], months: string[], n: number): { series: ChartSeriesDef[]; rows: Record<string, number | string>[] } => {
  const top = topNWithOthers(
    categories.map((c) => ({ name: c.name, color: c.color, amount_cents: c.total_cents, id: c.category_id ?? 0 })),
    n,
  );
  const topIds = new Set(top.filter((t) => 'id' in t).map((t) => (t as { id: number }).id));
  const hasOthers = top.some((t) => t.name === OTHERS_LABEL && !('id' in t));
  const series: ChartSeriesDef[] = top.map((t) => ('id' in t ? { key: `c${(t as { id: number }).id}`, name: t.name, color: t.color } : { key: 'others', name: OTHERS_LABEL, color: 'slate' }));
  const rows = months.map((m, i) => {
    const row: Record<string, number | string> = { label: monthKeyLabel(m).replace(/\/\d{4}$/, '') };
    let others = 0;
    for (const c of categories) {
      const id = c.category_id ?? 0;
      const v = c.month_amounts[i] ?? 0;
      if (topIds.has(id)) row[`c${id}`] = v;
      else others += v;
    }
    if (hasOthers) row.others = others;
    return row;
  });
  return { series, rows };
};

// ---------------------------------------------------------------- fluxo de caixa

/**
 * Pontos do gráfico de saldo final: `realized` nos meses passados e no atual, `projected` no atual
 * e nos futuros — o mês atual aparece nas duas séries para unir as linhas; `null` interrompe.
 */
export const cashflowChartRows = (months: CashflowMonth[]): Record<string, number | string | null>[] =>
  months.map((m) => ({
    label: m.label,
    realized: m.is_future ? null : m.closing_cents,
    projected: m.is_future || m.is_current ? m.closing_cents : null,
  }));

// ---------------------------------------------------------------- anos

/** Anos oferecidos no relatório anual: 5 para trás e 1 para a frente do ano corrente (mais o escolhido), desc. */
export const yearOptions = (selected: number, now: Date = new Date()): number[] => {
  const y = now.getFullYear();
  const set = new Set<number>([selected]);
  for (let i = y - 5; i <= y + 1; i++) set.add(i);
  return [...set].sort((a, b) => b - a);
};
